import { useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { DsButton, DsInput, DsModal } from '@/modules/internal-v2/ds';
import type { ItemWithAllPrices } from '@/api/items/items.types';
import {
  useEditItemSpecs,
  usePickerProductLines,
  usePickerProductModels,
  usePickerVocabulary,
} from '@/api/specifications/specifications.hooks';
import type {
  DeviceCategory,
  EditItemSpecsRequest,
  IdentityChange,
  SpecValueChange,
} from '@/api/specifications/specifications.types';
import {
  ATTRIBUTE_SPECS_BY_CATEGORY,
  type AttributeSpec,
} from './spec-attribute-config';
import './spec-edit-modal.css';

const CATEGORIES: { value: DeviceCategory; label: string }[] = [
  { value: 'smartphone', label: 'Smartphone' },
  { value: 'tablet', label: 'Tablet' },
  { value: 'smartwatch', label: 'Smartwatch' },
  { value: 'notebook', label: 'Notebook' },
];

interface SpecEditModalProps {
  open: boolean;
  item: ItemWithAllPrices;
  onClose: () => void;
}

/**
 * Single-item specification editor. Lets an admin override the AI-extracted
 * identity (productLine, model) and every per-category spec value. Pickers
 * can also *propose* new productLines / models / vocabulary — those land in
 * the review queue alongside the AI's own proposals.
 *
 * The device category itself is read-only: it is derived from the item's
 * SAP `item_group.device_category` and surfaced here for context only.
 */
export function SpecEditModal({ open, item, onClose }: SpecEditModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const edit = useEditItemSpecs();

  // The parent always renders the modal as `{editing && <SpecEditModal />}`,
  // so it remounts per opened item. That means initialising state from the
  // item once (in useState's initial value) is correct — no effect needed.
  const initial = useMemo(() => initialState(item), [item]);
  const category = initial.category;
  const [productLine, setProductLine] = useState<IdentityDraft>(
    initial.productLine,
  );
  const [model, setModel] = useState<IdentityDraft>(initial.model);
  const [specs, setSpecs] = useState<Record<string, SpecDraft>>(
    initial.specifications,
  );

  const attributeSpecs = category ? ATTRIBUTE_SPECS_BY_CATEGORY[category] : [];

  const handleSave = async () => {
    const body = buildRequest({ initial, productLine, model, specs });
    if (!hasChanges(body)) {
      enqueueSnackbar('Keine Änderungen.', { variant: 'info' });
      return;
    }
    try {
      const result = await edit.mutateAsync({ itemId: item.id, body });
      const proposedCount =
        result.proposed.productLines.length +
        result.proposed.models.length +
        result.proposed.vocabulary.length;
      enqueueSnackbar(
        proposedCount > 0
          ? `Gespeichert. ${proposedCount} neue Vorschläge benötigen Review.`
          : 'Gespeichert.',
        { variant: 'success' },
      );
      onClose();
    } catch (e) {
      enqueueSnackbar(
        `Speichern fehlgeschlagen: ${(e as Error).message}`,
        { variant: 'error' },
      );
    }
  };

  return (
    <DsModal
      open={open}
      onClose={onClose}
      title={`Spezifikationen bearbeiten · ${item.name}`}
      description={`Hersteller: ${item.manufacturer} · Art.-Nr. ${item.id}`}
      footer={
        <>
          <DsButton variant="ghost" onClick={onClose} disabled={edit.isPending}>
            Abbrechen
          </DsButton>
          <DsButton
            variant="primary"
            onClick={handleSave}
            disabled={edit.isPending}
          >
            {edit.isPending ? 'Speichert…' : 'Speichern'}
          </DsButton>
        </>
      }
    >
      <div className="spec-edit-form">
        <section className="spec-edit-section">
          <h3>Identität</h3>
          <div className="spec-edit-row">
            <label>Kategorie</label>
            <div className="spec-edit-readonly">
              {category ? labelFor(category) : '— keine (Warengruppe nicht zugeordnet)'}
            </div>
          </div>

          <div className="spec-edit-row">
            <label>Produktlinie</label>
            <ProductLinePicker
              manufacturerId={item.manufacturerId}
              category={category}
              value={productLine}
              onChange={(v) => {
                setProductLine(v);
                // Picking/clearing a new line invalidates the current model.
                setModel({ kind: 'clear' });
              }}
            />
          </div>

          <div className="spec-edit-row">
            <label>Modell</label>
            <ProductModelPicker
              productLine={productLine}
              value={model}
              onChange={setModel}
            />
          </div>
        </section>

        <section className="spec-edit-section">
          <h3>
            Eigenschaften{category ? ` · ${labelFor(category)}` : ''}
          </h3>
          {!category && (
            <p className="spec-edit-hint">
              Diese Warengruppe ist keiner Gerätekategorie zugeordnet —
              Identität & Eigenschaften können nicht bearbeitet werden.
            </p>
          )}
          {category &&
            attributeSpecs.map((spec) => (
              <SpecField
                key={spec.key}
                manufacturerId={item.manufacturerId}
                spec={spec}
                draft={specs[spec.key]}
                onChange={(d) =>
                  setSpecs((prev) => ({ ...prev, [spec.key]: d }))
                }
              />
            ))}
        </section>
      </div>
    </DsModal>
  );
}

// ── State shapes ────────────────────────────────────────────────────────

interface InitialState {
  category: DeviceCategory | null;
  productLine: IdentityDraft;
  model: IdentityDraft;
  specifications: Record<string, SpecDraft>;
  // captured baseline of identity ids so we know what to send
  initialProductLineId: number | null;
  initialModelId: number | null;
  initialSpecs: Record<string, unknown>;
}

type IdentityDraft =
  | { kind: 'unchanged'; id: number | null; label: string | null }
  | { kind: 'clear' }
  | { kind: 'select'; id: number; label: string }
  | { kind: 'propose'; name: string };

type SpecDraft =
  | { kind: 'unchanged' }
  | { kind: 'clear' }
  | { kind: 'mechanical'; value: string }
  | { kind: 'vocabulary-select'; id: number; label: string }
  | { kind: 'vocabulary-propose'; value: string };

function initialState(item: ItemWithAllPrices): InitialState {
  const cat = (item.identity?.category as DeviceCategory | undefined) ?? null;
  const pl = item.identity?.productLine;
  const md = item.identity?.model;
  const specObj = (item.specifications ?? {}) as Record<string, unknown>;
  const specifications: Record<string, SpecDraft> = {};
  for (const key of Object.keys(specObj)) {
    specifications[key] = { kind: 'unchanged' };
  }
  return {
    category: cat,
    productLine: {
      kind: 'unchanged',
      id: pl?.id ?? null,
      label: pl?.name ?? null,
    },
    model: {
      kind: 'unchanged',
      id: md?.id ?? null,
      label: md?.name ?? null,
    },
    specifications,
    initialProductLineId: pl?.id ?? null,
    initialModelId: md?.id ?? null,
    initialSpecs: specObj,
  };
}

function buildRequest(args: {
  initial: InitialState;
  productLine: IdentityDraft;
  model: IdentityDraft;
  specs: Record<string, SpecDraft>;
}): EditItemSpecsRequest {
  const { initial, productLine, model, specs } = args;
  const body: EditItemSpecsRequest = {};

  const lineChange = toIdentityChange(productLine, initial.initialProductLineId);
  if (lineChange) body.productLine = lineChange;

  const modelChange = toIdentityChange(model, initial.initialModelId);
  if (modelChange) body.model = modelChange;

  const specChanges: Record<string, SpecValueChange> = {};
  for (const [key, draft] of Object.entries(specs)) {
    const change = toSpecValueChange(draft);
    if (change) specChanges[key] = change;
  }
  if (Object.keys(specChanges).length > 0) body.specifications = specChanges;

  return body;
}

function toIdentityChange(
  draft: IdentityDraft,
  initialId: number | null,
): IdentityChange | undefined {
  if (draft.kind === 'unchanged') return undefined;
  if (draft.kind === 'clear') {
    return initialId == null ? undefined : { type: 'clear' };
  }
  if (draft.kind === 'select') {
    return draft.id === initialId ? undefined : { type: 'select', id: draft.id };
  }
  return { type: 'propose', name: draft.name.trim() };
}

function toSpecValueChange(draft: SpecDraft): SpecValueChange | undefined {
  switch (draft.kind) {
    case 'unchanged':
      return undefined;
    case 'clear':
      return { type: 'clear' };
    case 'mechanical':
      return { type: 'mechanical', value: draft.value };
    case 'vocabulary-select':
      return { type: 'vocabulary-select', id: draft.id };
    case 'vocabulary-propose':
      return { type: 'vocabulary-propose', value: draft.value };
  }
}

function hasChanges(body: EditItemSpecsRequest): boolean {
  return (
    body.productLine !== undefined ||
    body.model !== undefined ||
    (body.specifications && Object.keys(body.specifications).length > 0) ||
    false
  );
}

function labelFor(cat: DeviceCategory) {
  return CATEGORIES.find((c) => c.value === cat)?.label ?? cat;
}

// ── Pickers ─────────────────────────────────────────────────────────────

interface ProductLinePickerProps {
  manufacturerId: number;
  category: DeviceCategory | null;
  value: IdentityDraft;
  onChange: (v: IdentityDraft) => void;
}

function ProductLinePicker({
  manufacturerId,
  category,
  value,
  onChange,
}: ProductLinePickerProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const enabled = category != null;
  const { data: lines } = usePickerProductLines({
    manufacturerId,
    category: enabled ? category : undefined,
    search: query || undefined,
  });

  if (!enabled) {
    return (
      <div className="spec-edit-hint">
        Erst eine Kategorie wählen.
      </div>
    );
  }

  return (
    <SearchablePicker
      query={query}
      onQueryChange={setQuery}
      open={open}
      onOpenChange={setOpen}
      selectedLabel={selectedLabel(value)}
      isCleared={value.kind === 'clear'}
      candidates={lines ?? []}
      labelOf={(c) => c.name}
      stateOf={(c) => c.state}
      placeholder="Produktlinie wählen oder neu vorschlagen…"
      onSelect={(c) =>
        onChange({ kind: 'select', id: c.id, label: c.name })
      }
      onPropose={(name) => onChange({ kind: 'propose', name })}
      onClear={() => onChange({ kind: 'clear' })}
    />
  );
}

interface ProductModelPickerProps {
  productLine: IdentityDraft;
  value: IdentityDraft;
  onChange: (v: IdentityDraft) => void;
}

function ProductModelPicker({
  productLine,
  value,
  onChange,
}: ProductModelPickerProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  // Only enable when there is an existing line selected (we can't list models
  // for a yet-to-be-created proposed line).
  const lineId =
    productLine.kind === 'select'
      ? productLine.id
      : productLine.kind === 'unchanged'
        ? productLine.id ?? undefined
        : undefined;
  const enabled = lineId != null;

  const { data: models } = usePickerProductModels({
    productLineId: lineId,
    search: query || undefined,
  });

  if (productLine.kind === 'clear') {
    return <div className="spec-edit-hint">Produktlinie ist abgewählt.</div>;
  }
  if (!enabled) {
    // newly proposed line — let admin still propose a model
    return (
      <SearchablePicker
        query={query}
        onQueryChange={setQuery}
        open={open}
        onOpenChange={setOpen}
        selectedLabel={selectedLabel(value)}
        isCleared={value.kind === 'clear'}
        candidates={[]}
        labelOf={() => ''}
        stateOf={() => 'proposed'}
        placeholder="Modell-Namen vorschlagen…"
        onSelect={() => undefined}
        onPropose={(name) => onChange({ kind: 'propose', name })}
        onClear={() => onChange({ kind: 'clear' })}
      />
    );
  }

  return (
    <SearchablePicker
      query={query}
      onQueryChange={setQuery}
      open={open}
      onOpenChange={setOpen}
      selectedLabel={selectedLabel(value)}
      isCleared={value.kind === 'clear'}
      candidates={models ?? []}
      labelOf={(c) => c.name}
      stateOf={(c) => c.state}
      placeholder="Modell wählen oder neu vorschlagen…"
      onSelect={(c) => onChange({ kind: 'select', id: c.id, label: c.name })}
      onPropose={(name) => onChange({ kind: 'propose', name })}
      onClear={() => onChange({ kind: 'clear' })}
    />
  );
}

function selectedLabel(draft: IdentityDraft): string | null {
  switch (draft.kind) {
    case 'unchanged':
      return draft.label;
    case 'clear':
      return null;
    case 'select':
      return draft.label;
    case 'propose':
      return `${draft.name} (Vorschlag)`;
  }
}

interface SpecFieldProps {
  manufacturerId: number;
  spec: AttributeSpec;
  draft: SpecDraft | undefined;
  onChange: (d: SpecDraft) => void;
}

function SpecField({
  manufacturerId,
  spec,
  draft,
  onChange,
}: SpecFieldProps) {
  if (spec.class === 'mechanical-closed-enum') {
    return (
      <div className="spec-edit-row">
        <label>{labelForAttribute(spec.key)}</label>
        <ClosedEnumPicker
          spec={spec}
          draft={draft}
          onChange={onChange}
        />
      </div>
    );
  }
  if (spec.class === 'mechanical-numeric') {
    return (
      <div className="spec-edit-row">
        <label>{labelForAttribute(spec.key)}</label>
        <MechanicalInput
          spec={spec}
          draft={draft}
          onChange={onChange}
        />
      </div>
    );
  }
  return (
    <div className="spec-edit-row">
      <label>{labelForAttribute(spec.key)}</label>
      <VocabularyPicker
        manufacturerId={manufacturerId}
        spec={spec}
        draft={draft}
        onChange={onChange}
      />
    </div>
  );
}

function ClosedEnumPicker({
  spec,
  draft,
  onChange,
}: {
  spec: AttributeSpec;
  draft: SpecDraft | undefined;
  onChange: (d: SpecDraft) => void;
}) {
  const current = draft?.kind === 'mechanical' ? draft.value : null;
  const isCleared = draft?.kind === 'clear';
  return (
    <div className="spec-edit-enum">
      {(spec.enumValues ?? []).map((v) => {
        const active = current === v;
        return (
          <button
            key={v}
            type="button"
            className={`spec-edit-chip${active ? ' is-active' : ''}`}
            onClick={() => onChange({ kind: 'mechanical', value: v })}
          >
            {v}
          </button>
        );
      })}
      <button
        type="button"
        className={`spec-edit-chip is-clear${isCleared ? ' is-active' : ''}`}
        onClick={() => onChange({ kind: 'clear' })}
        title="Wert leeren"
      >
        —
      </button>
    </div>
  );
}

function MechanicalInput({
  spec,
  draft,
  onChange,
}: {
  spec: AttributeSpec;
  draft: SpecDraft | undefined;
  onChange: (d: SpecDraft) => void;
}) {
  const value =
    draft?.kind === 'mechanical'
      ? draft.value
      : draft?.kind === 'clear'
        ? ''
        : '';
  return (
    <div className="spec-edit-mechanical">
      <DsInput
        value={value}
        placeholder={hintFor(spec.key) ?? 'Wert eingeben…'}
        onChange={(e) => {
          const v = e.target.value;
          onChange(
            v === ''
              ? draft?.kind === 'clear'
                ? { kind: 'clear' }
                : { kind: 'unchanged' }
              : { kind: 'mechanical', value: v },
          );
        }}
      />
      <DsButton
        variant="ghost"
        onClick={() => onChange({ kind: 'clear' })}
        title="Wert leeren"
      >
        —
      </DsButton>
    </div>
  );
}

interface VocabularyPickerProps {
  manufacturerId: number;
  spec: AttributeSpec;
  draft: SpecDraft | undefined;
  onChange: (d: SpecDraft) => void;
}

function VocabularyPicker({
  manufacturerId,
  spec,
  draft,
  onChange,
}: VocabularyPickerProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const { data: vocab } = usePickerVocabulary({
    manufacturerId,
    attribute: spec.key,
    search: query || undefined,
  });

  const selectedLabelText =
    draft?.kind === 'vocabulary-select'
      ? draft.label
      : draft?.kind === 'vocabulary-propose'
        ? `${draft.value} (Vorschlag)`
        : null;

  return (
    <SearchablePicker
      query={query}
      onQueryChange={setQuery}
      open={open}
      onOpenChange={setOpen}
      selectedLabel={selectedLabelText}
      isCleared={draft?.kind === 'clear'}
      candidates={vocab ?? []}
      labelOf={(c) => c.value}
      stateOf={(c) => c.state}
      placeholder={`${labelForAttribute(spec.key)} wählen oder vorschlagen…`}
      onSelect={(c) =>
        onChange({ kind: 'vocabulary-select', id: c.id, label: c.value })
      }
      onPropose={(name) =>
        onChange({ kind: 'vocabulary-propose', value: name })
      }
      onClear={() => onChange({ kind: 'clear' })}
    />
  );
}

// ── Searchable Picker primitive ─────────────────────────────────────────

interface SearchablePickerProps<T> {
  query: string;
  onQueryChange: (q: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedLabel: string | null;
  isCleared: boolean;
  candidates: T[];
  labelOf: (c: T) => string;
  stateOf: (c: T) => 'proposed' | 'approved';
  placeholder: string;
  onSelect: (c: T) => void;
  onPropose: (name: string) => void;
  onClear: () => void;
}

function SearchablePicker<T extends { id: number }>({
  query,
  onQueryChange,
  open,
  onOpenChange,
  selectedLabel,
  isCleared,
  candidates,
  labelOf,
  stateOf,
  placeholder,
  onSelect,
  onPropose,
  onClear,
}: SearchablePickerProps<T>) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) onOpenChange(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onOpenChange]);

  const trimmed = query.trim();
  const exactMatch = candidates.some(
    (c) => labelOf(c).toLowerCase() === trimmed.toLowerCase(),
  );
  const showPropose = trimmed.length > 0 && !exactMatch;

  return (
    <div className="spec-edit-picker" ref={rootRef}>
      <div className="spec-edit-picker-input">
        <DsInput
          value={open ? query : (selectedLabel ?? (isCleared ? '— leer —' : query))}
          placeholder={placeholder}
          onFocus={() => {
            onOpenChange(true);
            if (!open) onQueryChange('');
          }}
          onChange={(e) => {
            onQueryChange(e.target.value);
            onOpenChange(true);
          }}
        />
        {(selectedLabel || isCleared) && (
          <button
            type="button"
            className="spec-edit-picker-x"
            onClick={onClear}
            title="Auswahl leeren"
          >
            ×
          </button>
        )}
      </div>
      {open && (
        <div className="spec-edit-picker-pop">
          {showPropose && (
            <button
              type="button"
              className="spec-edit-picker-item is-propose"
              onClick={() => {
                onPropose(trimmed);
                onOpenChange(false);
              }}
            >
              + „{trimmed}" vorschlagen
            </button>
          )}
          {candidates.slice(0, 30).map((c) => (
            <button
              key={c.id}
              type="button"
              className="spec-edit-picker-item"
              onClick={() => {
                onSelect(c);
                onOpenChange(false);
              }}
            >
              <span>{labelOf(c)}</span>
              {stateOf(c) === 'proposed' && (
                <span className="spec-edit-picker-tag">proposed</span>
              )}
            </button>
          ))}
          {candidates.length === 0 && !showPropose && (
            <div className="spec-edit-picker-empty">Keine Treffer.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Attribute labels + hints ───────────────────────────────────────────

const ATTRIBUTE_LABELS: Record<string, string> = {
  storage: 'Speicher',
  ram: 'RAM',
  year: 'Jahr',
  connectivity: 'Konnektivität',
  region: 'Region',
  condition: 'Zustand',
  color: 'Farbe',
  chip: 'Chipsatz',
  screenSize: 'Display',
  caseSize: 'Gehäusegröße',
  caseColor: 'Gehäusefarbe',
  caseMaterial: 'Gehäusematerial',
  band: 'Armband',
  bandSize: 'Armband-Größe',
  cpuCores: 'CPU-Kerne',
  gpuCores: 'GPU-Kerne',
  keyboardLayout: 'Tastaturlayout',
};

const ATTRIBUTE_HINTS: Record<string, string> = {
  storage: 'z. B. 256GB, 1TB',
  ram: 'z. B. 8GB, 32GB',
  screenSize: 'z. B. 13.6", 15"',
  caseSize: 'z. B. 42mm',
  year: 'z. B. 2024',
  cpuCores: 'z. B. 10-core',
  gpuCores: 'z. B. 16-core',
};

function labelForAttribute(key: string): string {
  return ATTRIBUTE_LABELS[key] ?? key;
}

function hintFor(key: string): string | null {
  return ATTRIBUTE_HINTS[key] ?? null;
}
